import React from "react";
import { DispatchProp } from "react-redux";
import SignaturePad, { IPointGroup } from "signature_pad";
import { useTheme } from "react-jss";
import { ControlProps } from "./BaseControl";
import useDimensionContext from "../../hooks/useDimensionContext";
import { getSettingString, getSettingBoolean } from "../../models/utilities/settingUtilities";
import useStyles from "./SignatureControl.styles";
import { FontAwesomeIcon } from "../shared/FontAwesome";
import Button from "../shared/Button";
import { ButtonChoice } from "../shared/ButtonChoice";
import useTranslatable from "../../hooks/useTranslatable";
import { useMentions } from "../../hooks/useMentions";
import { useValidation, useAnswerRequiredValidator } from "../../hooks/useValidation";
import { VariableDataType } from "../../models/enums/VariableDataType";

interface Props extends ControlProps, DispatchProp {}

type SizeType = "Small" | "Medium" | "Large" | "Full";

function resizeData(data: IPointGroup[], scale: number): IPointGroup[] {
    return data.map((pointGroup) => ({
        ...pointGroup,
        points: pointGroup.points.map((point) => ({
            ...point,
            x: point.x * scale,
            y: point.y * scale,
        })),
    }));
}

function maxSize(size: SizeType) {
    switch (size) {
        case "Small":
            return 300;
        case "Medium":
            return 500;
        case "Full":
            return 1000;
        default:
            return 800;
    }
}

function onChoiceKeyDown(event, onToggleNoAnswer) {
    if (event.target !== event.currentTarget) {
        return;
    }
    if (event.key === " ") {
        onToggleNoAnswer();
        event.preventDefault();
    }
}

export function SignatureControl(props: Props) {
    const { question, validateSignal } = props;

    const displayNoAnswer = getSettingBoolean(question, "DisplayNoAnswer");
    const noAnswerValue = getSettingString(question, "NoAnswerValue");
    const noAnswerLabel = useTranslatable(question, "NoAnswerLabel");

    const variable = question.variables[0];
    const canvasRef = React.useRef<HTMLCanvasElement>();
    const signatureRef = React.useRef<SignaturePad>();
    const { innerWidth } = useDimensionContext();
    const [mentions, setMentions] = useMentions(variable.name);
    const mentionValue = mentions[0] && mentions[0].value;
    const [hasLines, setHasLines] = React.useState(false);
    const [initialValue, setInitialValue] = React.useState(
        mentionValue !== noAnswerValue ? mentionValue : undefined,
    );

    const theme = useTheme();
    const ratio = 21 / 9;
    const signatureWidth = Math.min(
        innerWidth - 2,
        maxSize(getSettingString(question, "Size") as SizeType),
    );
    const classes = useStyles({ theme, signatureWidth, ratio });

    useValidation(validateSignal, useAnswerRequiredValidator(question, variable));

    const isSvg = variable.dataType === VariableDataType.Base64;

    React.useEffect(() => {
        if (initialValue) {
            return undefined;
        }
        let data;
        if (signatureRef.current) {
            data = signatureRef.current.toData();
        }
        const canvas = canvasRef.current;
        const prevCanvasWidth = canvas.width;
        canvas.width = signatureWidth;
        canvas.height = canvas.width / ratio;
        signatureRef.current = new SignaturePad(canvas, {
            penColor: getSettingString(question, "PenColor", "#000000"),
            minWidth: signatureWidth * 0.002,
            maxWidth: signatureWidth * 0.006,
            minDistance: signatureWidth * 0.006,
            onEnd: () => {
                setHasLines(true);
                setMentions((b) =>
                    b.set(signatureRef.current.toDataURL(isSvg ? "image/svg+xml" : null)),
                );
            },
        });
        if (data) {
            data = resizeData(data, canvas.width / prevCanvasWidth);
            signatureRef.current.fromData(data);
        }

        return () => {
            signatureRef.current.off();
        };
    }, [initialValue, innerWidth, isSvg, question, ratio, setMentions, signatureWidth]);

    const onToggleNoAnswer = React.useCallback(() => {
        signatureRef.current.clear();
        if (mentionValue === noAnswerValue) {
            setMentions((b) => b.clear());
        } else {
            setMentions((b) => b.set(noAnswerValue));
        }
        setHasLines(false);
    }, [mentionValue, noAnswerValue, setMentions]);

    return (
        <>
            <div className={classes.container}>
                <div className={classes.signatureArea}>
                    <div className={classes.topLeftCorner} />
                    <div className={classes.topRightCorner} />
                    <div className={classes.bottomLeftCorner} />
                    <div className={classes.bottomRightCorner} />
                    {hasLines && (
                        <div className={classes.undoButton}>
                            <Button
                                icon={["fas", "undo"]}
                                text="Undo"
                                displayText={false}
                                size={signatureWidth * 0.03}
                                onClick={() => {
                                    if (signatureRef.current) {
                                        const data = signatureRef.current.toData();
                                        signatureRef.current.clear();
                                        signatureRef.current.fromData(data.slice(0, -1));
                                        if (data.length > 1) {
                                            setMentions((b) =>
                                                b.set(
                                                    signatureRef.current.toDataURL(
                                                        isSvg ? "image/svg+xml" : null,
                                                    ),
                                                ),
                                            );
                                        } else {
                                            setMentions((m) => m.clear());
                                            setHasLines(false);
                                        }
                                    }
                                }}
                            />
                        </div>
                    )}
                    <div className={classes.clearButton}>
                        <Button
                            icon={["fas", "times"]}
                            text="Clear"
                            displayText={false}
                            size={signatureWidth * 0.03}
                            onClick={() => {
                                setMentions((b) => b.clear());
                                setInitialValue(undefined);
                                setHasLines(false);
                                if (signatureRef.current) {
                                    signatureRef.current.clear();
                                }
                            }}
                        />
                    </div>
                    <div className={classes.signatureLine}>
                        <FontAwesomeIcon icon="signature" fixedWidth />
                    </div>
                    {!initialValue && <canvas className={classes.canvas} ref={canvasRef} />}
                    {initialValue && <img className={classes.image} src={initialValue} alt="" />}
                </div>
            </div>
            {displayNoAnswer && (
                <ButtonChoice
                    type="checkbox"
                    onKeyDown={(event) => onChoiceKeyDown(event, onToggleNoAnswer)}
                    onSelect={onToggleNoAnswer}
                    selected={mentionValue === noAnswerValue}
                    label={noAnswerLabel}
                />
            )}
        </>
    );
}
